import { useEffect } from 'react';
import { DEPLOY_ENV, SITE, SETTINGS } from '../../site.config';
import { useGlobalStore } from './useGlobalStore';
import {
  AmplitudeTracker,
  DevTracker,
  GA4Tracker,
  GTMTracker,
  HeapTracker,
  MulticastTracker,
  Tracker,
} from 'services/tracker';

export function useTrackEvent(): Tracker {
  const {
    globalState: {
      apiState: { household },
      auth,
    },
  } = useGlobalStore();

  if (!window.tracker) {
    const trackers: Tracker[] = [];

    // Skip adding trackers in automated tests
    // @ts-ignore
    if (!window.AUTOMATED_TEST_ENV) {
      if (window.savviDebug) {
        trackers.push(new DevTracker());
      }

      if (window.dataLayer) {
        trackers.push(new GTMTracker());
      }

      const measurementId = (SETTINGS as any).ANALYTICS?.GA4_MEASUREMENT_ID;
      if (measurementId) {
        trackers.push(
          new GA4Tracker({
            measurementId,
          }),
        );
      }

      const amplitudeApiKey = (SETTINGS as any).ANALYTICS?.AMPLITUDE_API_KEY;
      if (amplitudeApiKey) {
        trackers.push(
          new AmplitudeTracker({
            apiKey: amplitudeApiKey,
          }),
        );
      }

      const heapAppId = (SETTINGS as any).ANALYTICS?.HEAP_APP_ID;
      if (heapAppId) {
        trackers.push(
          new HeapTracker({
            appId: heapAppId,
          }),
        );
      }
    }

    window.tracker = new MulticastTracker(trackers);
  }

  useEffect(() => {
    const siteData: Record<string, any> = {};
    if (SITE.ID === 'myvoyage') {
      siteData.externalId =
        household.external_ids?.voya_party_id ?? auth.metadata?.voya_party_id;
      siteData.clientId =
        household.external_ids?.voya_client_id ?? auth.metadata?.voya_client_id;
      siteData.planId =
        household.external_ids?.voya_plan_id ?? auth.metadata?.voya_plan_id;
    } else if (SITE.ID === 'benefitfocus') {
      siteData.externalId =
        household.external_ids?.bf_user_id ?? auth.user?.username;
      siteData.subscriberId = household.external_ids?.bf_subscriber_id;
      siteData.sponsorId = household.external_ids?.bf_sponsor_id;
    }

    window.tracker.config({
      // @ts-expect-error Property 'household_id' does not exist on type 'Household'.
      householdId: household.household_id ?? auth.user?.group_id ?? null,
      userId: auth.user?.id,
      guidanceMode: household?.guidance_mode,
      employerId: auth.metadata?.employer_id,
      employerName: auth.metadata?.employer_name,
      site: SITE.ID,
      appName: SITE.NAME,
      appVersion: DEPLOY_ENV,
      ...siteData,
    });
  }, [
    household.external_ids?.voya_client_id,
    household.external_ids?.voya_party_id,
    household.external_ids?.voya_plan_id,
    // @ts-expect-error Property 'household_id' does not exist on type 'Household'.
    household.household_id,
    auth.metadata?.voya_client_id,
    auth.metadata?.voya_party_id,
    auth.metadata?.voya_plan_id,
    auth.metadata?.employer_id,
    auth.metadata?.employer_name,
    auth.user?.group_id,
    auth.user?.id,
    household?.guidance_mode,
  ]);

  return window.tracker;
}
